import type { WorkflowStep } from "@/types/workflow.types";

export const NODE_WIDTH = 320;
export const HORIZONTAL_GAP = 160;
export const VIRTUAL_NODE_WIDTH = 132;
export const VIRTUAL_NODE_HEIGHT = 52;
const DAG_ESTIMATED_NODE_HEIGHT = 236;
const DAG_VERTICAL_GAP = 72;
const NODE_COLLISION_PADDING = 16;
const REAL_NODE_X_START = VIRTUAL_NODE_WIDTH + HORIZONTAL_GAP;
export interface DagLayout {
  stepPositions: Record<string, { x: number; y: number }>;
  startPosition: { x: number; y: number };
  endPosition: { x: number; y: number };
  stepById: Map<string, WorkflowStep>;
  roots: WorkflowStep[];
  leaves: WorkflowStep[];
}
interface DagGraph {
  stepById: Map<string, WorkflowStep>;
  incoming: Map<string, WorkflowStep[]>;
  outgoing: Map<string, WorkflowStep[]>;
  roots: WorkflowStep[];
  leaves: WorkflowStep[];
}
export function buildDagLayout(steps: WorkflowStep[]): DagLayout {
  const graph = buildDagGraph(steps);
  const shouldAutoLayout =
    !allStepsHaveUiPosition(steps) || hasDagPositionCollisions(steps);
  const layout = shouldAutoLayout
    ? buildDagAutoLayout(steps, graph)
    : buildDagPositionLayout(steps, graph);
  return {
    ...layout,
    stepById: graph.stepById,
    roots: graph.roots,
    leaves: graph.leaves,
  };
}
function getUiPosition(step: WorkflowStep): { x: number; y: number } | null {
  const x = step.uiPosition?.x;
  const y = step.uiPosition?.y;
  if (typeof x !== "number" || typeof y !== "number") return null;
  return { x, y };
}
function allStepsHaveUiPosition(steps: WorkflowStep[]): boolean {
  return steps.every((step) => Boolean(getUiPosition(step)));
}
function hasDagPositionCollisions(steps: WorkflowStep[]): boolean {
  const positioned = steps
    .map((step) => ({ step, position: getUiPosition(step) }))
    .filter((item): item is { step: WorkflowStep; position: { x: number; y: number } } =>
      Boolean(item.position)
    );

  if (positioned.length !== steps.length) return true;
  for (let i = 0; i < positioned.length; i += 1) {
    for (let j = i + 1; j < positioned.length; j += 1) {
      const a = positioned[i].position;
      const b = positioned[j].position;
      const overlapsX = isOverlapping(a.x, b.x, NODE_WIDTH);
      const overlapsY = isOverlapping(a.y, b.y, DAG_ESTIMATED_NODE_HEIGHT);
      if (overlapsX && overlapsY) return true;
    }
  }
  return false;
}
function isOverlapping(a: number, b: number, size: number): boolean {
  return a < b + size + NODE_COLLISION_PADDING && a + size + NODE_COLLISION_PADDING > b;
}
function buildDagGraph(steps: WorkflowStep[]): DagGraph {
  const stepById = new Map(steps.map((step) => [step.id, step]));
  const outgoing = new Map<string, WorkflowStep[]>();
  const incoming = new Map<string, WorkflowStep[]>();

  for (const step of steps) {
    const inputs = (step.inputStepIds ?? [])
      .map((id) => stepById.get(id))
      .filter((item): item is WorkflowStep => Boolean(item));
    incoming.set(step.id, inputs);
    for (const input of inputs) {
      const next = outgoing.get(input.id) ?? [];
      next.push(step);
      outgoing.set(input.id, next);
    }
  }
  const roots = steps.filter((step) => (incoming.get(step.id) ?? []).length === 0);
  const leaves = steps.filter((step) => (outgoing.get(step.id) ?? []).length === 0);
  return {
    stepById,
    incoming,
    outgoing,
    roots: roots.length > 0 ? roots : steps.slice(0, 1),
    leaves: leaves.length > 0 ? leaves : steps.slice(-1),
  };
}
function buildDagAutoLayout(steps: WorkflowStep[], graph: DagGraph) {
  const levels = new Map<string, number>();
  const visiting = new Set<string>();

  const resolveLevel = (step: WorkflowStep): number => {
    const cached = levels.get(step.id);
    if (cached) return cached;
    if (visiting.has(step.id)) return Math.max(1, step.stepOrder);

    visiting.add(step.id);
    const parents = graph.incoming.get(step.id) ?? [];
    const level =
      parents.length === 0
        ? 1
        : Math.max(...parents.map((parent) => resolveLevel(parent))) + 1;
    visiting.delete(step.id);
    levels.set(step.id, level);
    return level;
  };
  for (const step of steps) resolveLevel(step);
  const grouped = groupByLevel(steps, levels);
  const maxRows = Math.max(...Array.from(grouped.values()).map((group) => group.length));
  const layoutHeight =
    maxRows * DAG_ESTIMATED_NODE_HEIGHT + Math.max(0, maxRows - 1) * DAG_VERTICAL_GAP;
  const stepPositions: Record<string, { x: number; y: number }> = {};
  for (const [level, group] of grouped) {
    const sortedGroup = [...group].sort((a, b) => a.stepOrder - b.stepOrder);
    const groupHeight =
      sortedGroup.length * DAG_ESTIMATED_NODE_HEIGHT +
      Math.max(0, sortedGroup.length - 1) * DAG_VERTICAL_GAP;
    const yOffset = Math.max(0, (layoutHeight - groupHeight) / 2);
    for (const [index, step] of sortedGroup.entries()) {
      stepPositions[step.id] = {
        x: REAL_NODE_X_START + (level - 1) * (NODE_WIDTH + HORIZONTAL_GAP),
        y: yOffset + index * (DAG_ESTIMATED_NODE_HEIGHT + DAG_VERTICAL_GAP),
      };
    }
  }
  const maxLevel = Math.max(...Array.from(levels.values()));
  const virtualY = Math.max(0, layoutHeight / 2 - VIRTUAL_NODE_HEIGHT / 2);
  return {
    stepPositions,
    startPosition: { x: 0, y: virtualY },
    endPosition: {
      x: REAL_NODE_X_START + maxLevel * (NODE_WIDTH + HORIZONTAL_GAP),
      y: virtualY,
    },
  };
}
function groupByLevel(steps: WorkflowStep[], levels: Map<string, number>) {
  const grouped = new Map<number, WorkflowStep[]>();
  for (const step of steps) {
    const level = levels.get(step.id) ?? 1;
    const bucket = grouped.get(level) ?? [];
    bucket.push(step);
    grouped.set(level, bucket);
  }
  return grouped;
}
function buildDagPositionLayout(steps: WorkflowStep[], graph: DagGraph) {
  const stepPositions: Record<string, { x: number; y: number }> = {};
  for (const step of steps) {
    const position = getUiPosition(step);
    if (position) stepPositions[step.id] = position;
  }
  const minRootX = Math.min(...graph.roots.map((step) => stepPositions[step.id]?.x ?? 0));
  const maxLeafX = Math.max(...graph.leaves.map((step) => stepPositions[step.id]?.x ?? 0));
  return {
    stepPositions,
    startPosition: {
      x: minRootX - HORIZONTAL_GAP - VIRTUAL_NODE_WIDTH,
      y: centerY(graph.roots, stepPositions),
    },
    endPosition: {
      x: maxLeafX + NODE_WIDTH + HORIZONTAL_GAP,
      y: centerY(graph.leaves, stepPositions),
    },
  };
}
function centerY(
  steps: WorkflowStep[],
  positions: Record<string, { x: number; y: number }>
): number {
  if (steps.length === 0) return 0;
  const average =
    steps.reduce(
      (sum, step) => sum + (positions[step.id]?.y ?? 0) + DAG_ESTIMATED_NODE_HEIGHT / 2,
      0
    ) / steps.length;
  return average - VIRTUAL_NODE_HEIGHT / 2;
}
